/**
 * Turning a raw order book into the handful of rows a person can actually read.
 *
 * The book carries one level per distinct price, and a single market maker can
 * rest a thousand orders across a thousand prices. Rendering one row each makes
 * the panel unusable: the sides are different lengths, so the spread drifts off
 * centre, and the deeper side scrolls for pages.
 *
 * Two things fix that, and both live here so they can be tested without a DOM:
 *
 *   1. Cluster prices into ticks, so nearby levels merge into one row.
 *   2. Cap how many rows each side contributes, so the two sides are the same
 *      length and the spread sits in the middle by construction.
 */

/** One price level as it arrives from the book. */
export interface RawLevel {
  price: number;
  size: number;
}

/** One row as displayed: possibly several raw levels merged into one tick. */
export interface DisplayLevel {
  /** The tick this row covers. */
  price: number;
  /** Total size resting inside the tick. */
  size: number;
  /**
   * Cumulative size from the best price through this tick.
   *
   * Computed across EVERY tick before the cap is applied, so the last visible
   * row still states the true depth through its price rather than the depth of
   * the rows that happened to fit.
   */
  total: number;
  /** How many raw levels merged into this row. 1 means nothing was merged. */
  merged: number;
}

/** A size change at one price, and when it was seen. */
export interface LevelChange {
  /** Positive when liquidity was added at this price, negative when pulled. */
  delta: number;
  /** Wall-clock ms the change was observed. */
  atMs: number;
}

/** How long a change stays on screen before it stops being news. */
export const DELTA_TTL_MS = 4000;

/** Rows per side. Both sides use the same cap, which is what keeps the spread
 * centred without any scroll correction. */
export const DEFAULT_LEVEL_CAP = 12;

/**
 * Decimal places needed to write a tick exactly.
 *
 * Ticks are powers of ten, so this is just the negated exponent, floored at
 * zero for ticks of 1 or more.
 */
export function tickDecimals(tick: number): number {
  if (!Number.isFinite(tick) || tick <= 0) return 6;
  return Math.max(0, Math.min(12, -Math.floor(Math.log10(tick))));
}

/** Round to the precision the tick implies, so a bucket price reads as
 * `0.6265` rather than `0.6264999999999999`. */
function roundToTick(value: number, tick: number): number {
  return Number(value.toFixed(tickDecimals(tick)));
}

/**
 * The default tick for a market, at roughly 0.1%-1% of price.
 *
 * Derived from the price's magnitude alone, NOT from the levels currently on
 * the book. A tick that adapts to live depth changes as orders arrive, which
 * re-buckets every row and makes the panel jump while someone is reading it.
 */
export function defaultTick(referencePrice: number): number {
  if (!Number.isFinite(referencePrice) || referencePrice <= 0) return 0.01;
  return 10 ** (Math.floor(Math.log10(referencePrice)) - 2);
}

/**
 * Selectable ticks for a market, coarsest last.
 *
 * The finest entry is two decades below the default, which is finer than any
 * realistic level spacing -- that is the "don't cluster" option.
 */
export function tickLadder(referencePrice: number): number[] {
  const base = Math.log10(defaultTick(referencePrice));
  const exponent = Math.round(base);
  return [-2, -1, 0, 1, 2].map((offset) => 10 ** (exponent + offset));
}

/** Write a tick for a `<select>` label. */
export function formatTick(tick: number): string {
  return tick.toFixed(tickDecimals(tick));
}

/**
 * Cluster `raw` into ticks and return at most `cap` rows, best price first.
 *
 * Bids round DOWN to their tick and asks round UP, so a tick never straddles
 * the spread. Rounding both the same way would let the best bid's bucket and
 * the best ask's bucket meet at one price, showing a crossed book that is not
 * there.
 *
 * A `tick` of zero or less means no clustering: each price keeps its own row.
 */
export function aggregateLevels(
  raw: readonly RawLevel[],
  side: "bid" | "ask",
  tick: number,
  cap: number = DEFAULT_LEVEL_CAP,
): DisplayLevel[] {
  const usable = raw.filter(
    (l) => Number.isFinite(l.price) && Number.isFinite(l.size) && l.size > 0,
  );
  if (usable.length === 0) return [];

  const clustering = Number.isFinite(tick) && tick > 0;
  const buckets = new Map<number, { size: number; merged: number }>();

  for (const level of usable) {
    let price: number;
    if (!clustering) {
      price = level.price;
    } else {
      // The epsilon keeps a price sitting exactly on a tick boundary in its own
      // bucket. Without it, floating-point error in `price / tick` pushes such
      // a price a whole tick away, and the best bid or ask moves for no reason.
      const index =
        side === "bid"
          ? Math.floor(level.price / tick + 1e-9)
          : Math.ceil(level.price / tick - 1e-9);
      price = roundToTick(index * tick, tick);
    }
    const existing = buckets.get(price);
    if (existing) {
      existing.size += level.size;
      existing.merged += 1;
    } else {
      buckets.set(price, { size: level.size, merged: 1 });
    }
  }

  const sorted = [...buckets.entries()].sort((a, b) =>
    side === "bid" ? b[0] - a[0] : a[0] - b[0],
  );

  // Accumulate over every bucket, then cap. Doing it the other way round would
  // make the deepest visible row understate the book.
  let cumulative = 0;
  const all: DisplayLevel[] = sorted.map(([price, { size, merged }]) => {
    cumulative += size;
    return { price, size, total: cumulative, merged };
  });

  return cap > 0 ? all.slice(0, cap) : all;
}

/** Index one side's rows by price, for diffing against the next update. */
export function sizesByPrice(levels: readonly DisplayLevel[]): Map<number, number> {
  return new Map(levels.map((l) => [l.price, l.size]));
}

/**
 * What changed on one side since the last update.
 *
 * Returns the changes worth showing right now: every price whose resting size
 * moved in THIS update, plus changes from earlier updates that have not yet
 * aged out. A price that stops moving keeps its last change until `ttlMs`
 * passes, then goes quiet -- so the column reads as "what just happened"
 * rather than a permanent number nobody can date.
 *
 * A price appearing for the first time counts as its whole size added. A price
 * that disappears is not reported: it has no row left to render the change on.
 *
 * `previous` empty means there is nothing to compare against -- the first paint
 * after a subscribe, a market change or a tick change. Every price would
 * otherwise register as freshly added and the whole book would light up green
 * for something that merely came into view, so that case reports nothing.
 */
export function diffLevels(
  previous: ReadonlyMap<number, number>,
  next: readonly DisplayLevel[],
  atMs: number,
  carried: ReadonlyMap<number, LevelChange>,
  ttlMs: number = DELTA_TTL_MS,
): Map<number, LevelChange> {
  const out = new Map<number, LevelChange>();

  // Carry forward anything still within its lifetime, so a one-off change stays
  // readable across the updates that follow it.
  for (const [price, change] of carried) {
    if (atMs - change.atMs < ttlMs) out.set(price, change);
  }

  if (previous.size === 0) return out;

  for (const level of next) {
    const before = previous.get(level.price);
    const delta = before === undefined ? level.size : level.size - before;
    if (delta === 0) continue;
    out.set(level.price, { delta, atMs });
  }
  return out;
}

/** The changes still worth showing at `nowMs`. */
export function liveChanges(
  changes: ReadonlyMap<number, LevelChange>,
  nowMs: number,
  ttlMs: number = DELTA_TTL_MS,
): Map<number, number> {
  const out = new Map<number, number>();
  for (const [price, change] of changes) {
    if (nowMs - change.atMs < ttlMs) out.set(price, change.delta);
  }
  return out;
}

/**
 * Write a size for a narrow column: never more than six characters of digits,
 * so two columns cannot run into each other.
 *
 * Clustering sums sizes, so displayed magnitudes are much larger than any one
 * order and full precision no longer fits. Below 1000 the value keeps two
 * decimals; above it, a unit suffix replaces them.
 */
export function formatCompactSize(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  const magnitude = Math.abs(value);
  if (magnitude < 1000) {
    return `${sign}${magnitude.toFixed(2)}`;
  }
  const units: [number, string][] = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "k"],
  ];
  for (const [scale, suffix] of units) {
    if (magnitude >= scale) {
      const scaled = magnitude / scale;
      // Keep three significant figures: 1.23k, 12.3k, 123k.
      const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
      return `${sign}${scaled.toFixed(decimals)}${suffix}`;
    }
  }
  return `${sign}${magnitude.toFixed(2)}`;
}
